import { createApp } from 'petite-vue'

/**
 * Post interface. Does not include all properties,
 * but can be adapted from below link as necessary.
 *
 * @see https://developer.wordpress.org/rest-api/reference/posts/
 */
interface Post {
  id: number
  title: {
    rendered: string
  }
  content: {
    rendered: string
  }
  sticky: boolean
}

/**
 * An app representing dynamic blog posts fetched from
 * the WordPress REST API using petite-vue.
 *
 * @see https://github.com/vuejs/petite-vue
 * @see https://developer.wordpress.org/rest-api/reference/posts/
 */
const blogPosts = createApp( {
  posts: <Post[]>[],
  currentPage: 1,
  totalPages: 1,
  queryArgs: {
    postsPerPage: 100,
    embed: true
  },
  stickyPosts: <Post[]> [],
  get notStickyPosts() {
    return this.posts.filter( ( post: Post ) => ! post.sticky )
  },
  get isLastPage() {
    return this.currentPage >= this.totalPages
  },
  error: null,
  getAllPosts() {
    this.getPosts()
    this.getStickyPosts()
  },
  getPosts() {
    fetch( `/wp-json/wp/v2/posts?per_page=${this.queryArgs.postsPerPage}&_embed=${this.queryArgs.embed}` )
      .then( response => {
        if ( ! response.ok ) {
          throw new Error( `HTTP error! Status: ${response.status}` )
        }
        if ( response.headers.has( 'X-WP-TotalPages' ) ) {
          this.totalPages = response.headers.get( 'X-WP-TotalPages' )
        }
        return response.json()
      } )
      .then( posts => this.posts = posts )
      .catch( error => this.error = error.message )
  },
  /**
   * Get sticky posts separately so that they will always
   * be included independently of the current page.
   */
  getStickyPosts() {
    fetch( `/wp-json/wp/v2/posts?per_page=${this.queryArgs.postsPerPage}&sticky=true&_embed=${this.queryArgs.embed}` )
      .then( response => {
        if ( ! response.ok ) {
          throw new Error( `HTTP error! Status: ${response.status}` )
        }
        return response.json()
      } )
      .then( posts => this.stickyPosts = posts )
      .catch( error => this.error = error.message )
  },
  /**
   * Get more posts.
   */
  getMorePosts() {
    fetch( `/wp-json/wp/v2/posts?per_page=${this.queryArgs.postsPerPage}&page=${this.currentPage + 1}&_embed=${this.queryArgs.embed}` )
      .then( response => {
        if ( ! response.ok ) {
          throw new Error( `HTTP error! Status: ${response.status}` )
        }
        console.log( response.headers )
        if ( response.headers.has( 'X-WP-TotalPages' ) ) {
          this.totalPages = response.headers.get( 'X-WP-TotalPages' )
          if ( this.totalPages > this.currentPage ) {
            this.currentPage += 1
          }
        }
        return response.json()
      } )
      .then( posts => this.posts = this.posts.concat( posts ) )
      .catch( error => this.error = error.message )
  },
  dateOptions: {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  },
  dateString( date: string ) {
    return new Date( date ).toLocaleDateString( 'en-US', this.dateOptions )
  }
} )

export default blogPosts